import * as React from 'react';
import isNil from 'lodash/isNil';

import ActionButton from 'components/buttons/action-button/ActionButton';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import CampaignButtonDropdown from 'modules/campaigns/components/campaign-button-dropdown/CampaignButtonDropdown';

import { __t, applyTranslations } from 'utils/language';
import { logError } from 'utils/log';
import { isBrandSettingsPageInComplete } from 'utils/organization';

import './campaigns-create-mixed.scss';

applyTranslations(require('./campaigns-create-mixed.i18n.json'));

import type {
  OrganizationSettings
} from 'types';

type Props = {
  brandProfileId: number | null | undefined,
  organizationSettings: OrganizationSettings,
  hasCampaigns: boolean,
  withCcm: boolean,
  withBrand: boolean,
  isCcmCreatorlessEnabled: boolean
};

const MSG_KEY = 'campaigns_create_mixed';

const CampaignsCreateMixed: React.FC<Props> = ({
  brandProfileId,
  organizationSettings,
  hasCampaigns,
  withCcm,
  withBrand,
  isCcmCreatorlessEnabled
}): JSX.Element => {
  if (isNil(brandProfileId)) {
    logError(__t(`${MSG_KEY}.errors.button_cannot_be_shown`));
    return <span />;
  }

  const settingsPageIncomplete = isBrandSettingsPageInComplete(organizationSettings);

  const renderButtonLabel = (): React.ReactNode => {
    const label = __t(`${MSG_KEY}.buttons.${hasCampaigns ? 'new_campaign' : 'first_campaign'}`);

    if (hasCampaigns === false) {
      return (
        <div className="campaigns-create-mixed__label">
          {label}
        </div>
      );
    }

    return (
      <div className="campaigns-create-mixed__label">
        <SVGDynamicIcon
          name="plus-custom-fg"
          width={13}
          height={13}
          customColor="white"
          className="campaigns-create-mixed__label-icon"
        />
        <div className="campaigns-create-mixed__label-text">
          {label}
        </div>
      </div>
    );
  };

  const renderActionButton = (
    link: string
  ): React.ReactNode => (
    <ActionButton
      key={`button-${settingsPageIncomplete ? 'disabled' : 'enabled'}${hasCampaigns ? '' : '-empty'}`}
      color="primary"
      link={link}
      disabled={settingsPageIncomplete}
    >
      {renderButtonLabel()}
    </ActionButton>
  );

  const renderCampaignButton = (): React.ReactNode => {
    if (withCcm && withBrand) {
      return (
        <CampaignButtonDropdown
          brandProfileId={brandProfileId}
          disabled={settingsPageIncomplete}
          withOffsiteCampaign={withBrand}
          withCreatorCampaign={withCcm}
        />
      );
    }

    if (withBrand) {
      return renderActionButton(`/brand/profiles/${brandProfileId}/campaigns/create`);
    }

    const ccm = isCcmCreatorlessEnabled
      ? '/ccm/select-campaign-type'
      : '/ccm/create?campaign_type=creatorfull';

    return renderActionButton(`/brand/profiles/${brandProfileId}/campaigns${ccm}`);
  };

  return (
    <div className="campaigns-create-mixed">
      {renderCampaignButton()}
    </div>
  );
};

export default CampaignsCreateMixed;
